import { includesForSearch } from '../../helpers/normalizeForSearch';
import { MatchVm } from '../../utils/api';
import { ClubMatchView, MatchResult, toClubMatchView } from './insightsClubSummary';
import { matchDateKey } from './insightsMatchBundleFilters';

/**
 * Client-side filters for the Insights Matches tab.
 * null / empty = all for each dimension; dimensions AND together.
 */

export type MatchOutcomeTag = MatchResult; // W | D | L
export type MatchVenueTag = 'home' | 'away';
export type MatchResultVenueTag = MatchOutcomeTag | MatchVenueTag;

export const MATCH_RESULT_VENUE_OPTIONS: { value: MatchResultVenueTag; label: string }[] = [
    { value: 'W', label: 'Win' },
    { value: 'D', label: 'Draw' },
    { value: 'L', label: 'Loss' },
    { value: 'home', label: 'Home' },
    { value: 'away', label: 'Away' },
];

export interface InsightsMatchListFilters {
    /** null = all competitions; [] = none. */
    competitionIds: number[] | null;
    dateFrom: string | null;
    dateTo: string | null;
    /** Home team id; null = any. */
    team1Id: number | null;
    /** Away team id; null = any. */
    team2Id: number | null;
    /** null = all; [] = none; else W/D/L and/or home/away. */
    resultVenue: MatchResultVenueTag[] | null;
    search: string;
}

export const emptyInsightsMatchListFilters = (): InsightsMatchListFilters => ({
    competitionIds: null,
    dateFrom: null,
    dateTo: null,
    team1Id: null,
    team2Id: null,
    resultVenue: null,
    search: '',
});

export interface MatchTeamOption {
    id: number;
    name: string;
}

/** Distinct home/away teams from the sample, sorted by name. */
export const collectMatchTeamOptions = (matches: MatchVm[]): MatchTeamOption[] => {
    const byId = new Map<number, string>();
    matches.forEach((match) => {
        if (match.homeTeamId != null) {
            byId.set(match.homeTeamId, match.homeTeamDisplayName || `Team ${match.homeTeamId}`);
        }
        if (match.awayTeamId != null) {
            byId.set(match.awayTeamId, match.awayTeamDisplayName || `Team ${match.awayTeamId}`);
        }
    });
    return Array.from(byId.entries())
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name));
};

const isAllResultVenue = (selected: MatchResultVenueTag[] | null): boolean => selected === null;

const matchesOutcomeVenue = (
    view: ClubMatchView,
    selected: MatchResultVenueTag[] | null
): boolean => {
    if (isAllResultVenue(selected)) return true;
    if (selected!.length === 0) return false;

    const outcomes = selected!.filter(
        (t): t is MatchOutcomeTag => t === 'W' || t === 'D' || t === 'L'
    );
    const venues = selected!.filter((t): t is MatchVenueTag => t === 'home' || t === 'away');

    if (outcomes.length > 0) {
        if (!view.result || !outcomes.includes(view.result)) return false;
    }
    if (venues.length > 0) {
        const ok =
            (venues.includes('home') && view.isHome) || (venues.includes('away') && !view.isHome);
        if (!ok) return false;
    }
    return true;
};

const matchesSearch = (match: MatchVm, view: ClubMatchView | undefined, q: string): boolean => {
    if (!q) return true;
    const hay = [
        match.homeTeamDisplayName,
        match.awayTeamDisplayName,
        match.competitionDisplayName,
        match.round,
        match.status,
        view?.opponentName,
        view?.result,
        match.homeTeamGoalsTotal != null && match.awayTeamGoalsTotal != null
            ? `${match.homeTeamGoalsTotal}:${match.awayTeamGoalsTotal}`
            : '',
    ]
        .filter(Boolean)
        .join(' ');
    return includesForSearch(hay, q);
};

export const filterClubMatches = (
    matches: MatchVm[],
    ourTeamIds: number[],
    filters: InsightsMatchListFilters
): MatchVm[] => {
    const q = filters.search.trim();
    const compsActive = filters.competitionIds !== null;
    const from = filters.dateFrom || null;
    const to = filters.dateTo || null;
    const hasDate = Boolean(from || to);

    return matches.filter((match) => {
        if (compsActive) {
            if (filters.competitionIds!.length === 0) return false;
            if (!filters.competitionIds!.includes(match.competitionId)) return false;
        }

        if (filters.team1Id != null && match.homeTeamId !== filters.team1Id) return false;
        if (filters.team2Id != null && match.awayTeamId !== filters.team2Id) return false;

        if (hasDate) {
            const key = matchDateKey(match.startDate);
            if (!key) return false;
            if (from && key < from) return false;
            if (to && key > to) return false;
        }

        const view = toClubMatchView(match, ourTeamIds);
        if (filters.resultVenue !== null) {
            if (!view) return false;
            if (!matchesOutcomeVenue(view, filters.resultVenue)) return false;
        }

        if (!matchesSearch(match, view, q)) return false;
        return true;
    });
};
